import { useEffect, useState } from "react";
import { portalApi } from "./PortalShell";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { TrendingUp, RotateCcw, Sparkles, Zap, Timer, Target } from "lucide-react";
import { toast } from "sonner";

// Presets for the ideal-client picker — coaches can also type a custom answer.
const IDEAL_CLIENTS = [
  "Fat-loss women 30-45",
  "Muscle-gain men 20-35",
  "Endurance / runners",
  "Post-natal recovery",
  "Vegan / vegetarian",
  "Older adults 45+",
  "Beginner / new to fitness",
];

const BLOCKERS = [
  "My content isn't converting to sales",
  "I'm not sure how to pitch supplements",
  "My audience is cold to promotions",
  "Clients switch to competitor brands",
  "I'm running out of ideas",
  "I don't have time to promote",
];

const TIME_OPTIONS = [
  { label: "5 min/day", value: 5 },
  { label: "10 min/day", value: 10 },
  { label: "15 min/day", value: 15 },
  { label: "30 min/day", value: 30 },
];

const IMPACT_STYLES = {
  high: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  medium: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  low: "bg-neutral-800 text-neutral-400 border-neutral-700",
};

const MD_COMPONENTS = {
  p: (p) => <p className="mb-1 leading-relaxed" {...p} />,
  ul: (p) => <ul className="list-disc pl-4 space-y-0.5 mb-1" {...p} />,
  li: (p) => <li {...p} />,
  strong: (p) => <strong className="text-white font-semibold" {...p} />,
};

export default function EarningsCoachSection() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  // Form state
  const [idealClient, setIdealClient] = useState(IDEAL_CLIENTS[0]);
  const [customClient, setCustomClient] = useState("");
  const [blocker, setBlocker] = useState(BLOCKERS[0]);
  const [customBlocker, setCustomBlocker] = useState("");
  const [minutes, setMinutes] = useState(15);
  const [tried, setTried] = useState("");

  useEffect(() => {
    portalApi
      .get("/affiliate/ai/earnings-coach/latest")
      .then((r) => setPlan(r.data.plan))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // If we already have a plan, prefill the form with the last answers so
  // regeneration feels like a refinement, not a restart.
  useEffect(() => {
    if (plan?.answers) {
      const a = plan.answers;
      if (IDEAL_CLIENTS.includes(a.ideal_client)) setIdealClient(a.ideal_client);
      else {
        setIdealClient("__custom__");
        setCustomClient(a.ideal_client);
      }
      if (BLOCKERS.includes(a.blocker)) setBlocker(a.blocker);
      else {
        setBlocker("__custom__");
        setCustomBlocker(a.blocker);
      }
      setMinutes(a.minutes_per_day || 15);
      setTried(a.tried_before || "");
    }
  }, [plan]);

  const submit = async (e) => {
    e?.preventDefault();
    const finalClient = (idealClient === "__custom__" ? customClient : idealClient).trim();
    const finalBlocker = (blocker === "__custom__" ? customBlocker : blocker).trim();
    if (!finalClient || !finalBlocker) {
      toast.error("Pick or type both an ideal client and a blocker");
      return;
    }
    setBusy(true);
    try {
      const { data } = await portalApi.post("/affiliate/ai/earnings-coach", {
        ideal_client: finalClient,
        blocker: finalBlocker,
        minutes_per_day: Number(minutes) || 15,
        tried_before: tried.trim(),
      });
      setPlan(data.plan);
      setShowForm(false);
      toast.success("Your tailored growth plan is ready");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't generate a plan. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const showFormNow = showForm || !plan;

  return (
    <div data-testid="earnings-coach-section" className="rounded-3xl bg-neutral-900 border border-neutral-800 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-neutral-800 bg-gradient-to-r from-orange-950/50 via-neutral-900 to-red-950/40 flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shrink-0">
          <TrendingUp className="h-5 w-5 text-white" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-orange-300 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> AI Growth Coach
          </div>
          <div className="text-lg font-bold tracking-tight">How To Increase Your Earnings</div>
        </div>
        {plan && !showFormNow && (
          <button
            onClick={() => setShowForm(true)}
            data-testid="earnings-coach-regen"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-neutral-400 hover:text-orange-300"
          >
            <RotateCcw className="h-3 w-3" /> Refresh
          </button>
        )}
      </div>

      {/* Body */}
      <div className="p-5">
        {loading && (
          <div className="text-sm text-neutral-500 italic">Loading your growth plan…</div>
        )}

        {!loading && showFormNow && (
          <form onSubmit={submit} className="space-y-4" data-testid="earnings-coach-form">
            <div className="text-[13px] text-neutral-400 leading-relaxed">
              Tell me 4 quick things about your business right now. I'll turn it into a tailored plan you can actually run this week.
            </div>

            {/* Q1 — Ideal client */}
            <Field label="1. Who's your ideal client right now?" icon={Target}>
              <select
                value={idealClient}
                onChange={(e) => setIdealClient(e.target.value)}
                data-testid="ec-ideal-client"
                className="w-full h-10 rounded-xl bg-neutral-950 border border-neutral-800 px-3 text-sm outline-none focus:border-orange-500"
              >
                {IDEAL_CLIENTS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
                <option value="__custom__">Other (I'll type it)</option>
              </select>
              {idealClient === "__custom__" && (
                <input
                  value={customClient}
                  onChange={(e) => setCustomClient(e.target.value)}
                  placeholder="e.g. Corporate professionals looking to build strength"
                  className="mt-2 w-full h-10 rounded-xl bg-neutral-950 border border-neutral-800 px-3 text-sm outline-none focus:border-orange-500"
                  data-testid="ec-ideal-client-custom"
                />
              )}
            </Field>

            {/* Q2 — Blocker */}
            <Field label="2. What's holding your affiliate income back?" icon={Zap}>
              <select
                value={blocker}
                onChange={(e) => setBlocker(e.target.value)}
                data-testid="ec-blocker"
                className="w-full h-10 rounded-xl bg-neutral-950 border border-neutral-800 px-3 text-sm outline-none focus:border-orange-500"
              >
                {BLOCKERS.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
                <option value="__custom__">Other (I'll type it)</option>
              </select>
              {blocker === "__custom__" && (
                <input
                  value={customBlocker}
                  onChange={(e) => setCustomBlocker(e.target.value)}
                  placeholder="e.g. I don't know when to bring up supplements without feeling pushy"
                  className="mt-2 w-full h-10 rounded-xl bg-neutral-950 border border-neutral-800 px-3 text-sm outline-none focus:border-orange-500"
                  data-testid="ec-blocker-custom"
                />
              )}
            </Field>

            {/* Q3 — Time budget */}
            <Field label="3. How much time can you spend on this per day?" icon={Timer}>
              <div className="flex flex-wrap gap-2">
                {TIME_OPTIONS.map((t) => (
                  <button
                    type="button"
                    key={t.value}
                    onClick={() => setMinutes(t.value)}
                    data-testid={`ec-time-${t.value}`}
                    className={`px-3.5 py-1.5 rounded-full border text-xs font-medium transition-colors ${minutes === t.value ? "bg-orange-500 border-orange-400 text-white" : "bg-neutral-950 border-neutral-800 text-neutral-300 hover:border-orange-500/50"}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </Field>

            {/* Q4 — Tried before (optional) */}
            <Field label="4. What have you already tried that didn't move the needle? (optional)" icon={RotateCcw}>
              <textarea
                value={tried}
                onChange={(e) => setTried(e.target.value)}
                rows={2}
                placeholder="e.g. Posted weekly reels with the code — no bump in orders"
                data-testid="ec-tried"
                className="w-full rounded-xl bg-neutral-950 border border-neutral-800 px-3 py-2 text-sm outline-none focus:border-orange-500 resize-y"
              />
            </Field>

            <div className="flex items-center gap-2 pt-1">
              <button
                type="submit"
                disabled={busy}
                data-testid="earnings-coach-submit"
                className="inline-flex items-center gap-2 rounded-xl bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold"
              >
                <Sparkles className="h-4 w-4" />
                {busy ? "Building your plan…" : plan ? "Refresh my growth plan" : "Get my growth plan"}
              </button>
              {plan && (
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="text-xs text-neutral-500 hover:text-neutral-300"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        )}

        {!loading && !showFormNow && plan && (
          <div className="space-y-3" data-testid="earnings-coach-plan">
            <div className="text-[11px] text-neutral-500 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span><b className="text-neutral-300">Ideal client:</b> {plan.answers.ideal_client}</span>
              <span><b className="text-neutral-300">Blocker:</b> {plan.answers.blocker}</span>
              <span><b className="text-neutral-300">Budget:</b> {plan.answers.minutes_per_day} min/day</span>
            </div>
            <div className="space-y-2.5 pt-1">
              {(plan.actions || []).map((a, i) => (
                <div
                  key={i}
                  data-testid={`earnings-coach-action-${i}`}
                  className="rounded-2xl bg-neutral-950 border border-neutral-800 p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-7 w-7 rounded-full bg-orange-500/15 border border-orange-500/30 flex items-center justify-center shrink-0 text-[11px] font-semibold text-orange-300">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <div className="text-sm font-semibold text-white leading-snug">{a.title}</div>
                        {a.impact && (
                          <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${IMPACT_STYLES[a.impact] || IMPACT_STYLES.medium}`}>
                            {a.impact}
                          </span>
                        )}
                      </div>
                      {a.why && (
                        <div className="text-[12px] text-neutral-400 mb-2 leading-relaxed">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{a.why}</ReactMarkdown>
                        </div>
                      )}
                      {Array.isArray(a.how) && a.how.length > 0 && (
                        <ul className="text-[12px] text-neutral-200 space-y-1 list-disc pl-4">
                          {a.how.map((step, j) => <li key={j}>{step}</li>)}
                        </ul>
                      )}
                      {a.time_estimate && (
                        <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-neutral-500">
                          <Timer className="h-3 w-3" /> {a.time_estimate}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-neutral-500 flex items-center gap-1.5 mb-1.5">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </label>
      {children}
    </div>
  );
}
